import { PaymentBalance, PaymentHistory, PaymentSources, VuexState } from '@/common/Payments';
import { PolicyUi } from '@/common/Policy';
import { RecurringPaymentsList } from '@/common/StaffPayments';
import BillPay from '@/services/BillPay';
import Recurring from '@/services/Recurring';
import Staff from '@/services/Staff';

const state: VuexState = {
	payment_sources: [],
	recurring_payment: [],
	payment_history: [],
	balance: {
		balance: '',
		due: '',
	},
	addedPaymentSuccess: false,
	removePaymentSuccess: false,
	verifyPaymentSuccess: false,
	recurring_data: [],
};

const getters = {
	payment_sources: (state: VuexState): PaymentSources[] => state.payment_sources,
	recurring_payment: (state: VuexState): any[] => state.recurring_payment,
	payment_history: (state: VuexState): PaymentHistory[] => state.payment_history,
	balance: (state: VuexState): PaymentBalance => state.balance,

	recurring_data: (state: VuexState): RecurringPaymentsList[] => state.recurring_data,
};

const mutations = {
	setPaymentSuccess: (
		state: VuexState,
		payload: { type: 'added' | 'remove' | 'verify'; status: boolean },
	): void => {
		state[payload.type + 'paymentSuccess'] = payload.status;

		// After 5 seconds, hide the alert
		setTimeout(() => {
			state[payload.type + 'paymentSuccess'] = false;
		}, 5000);
	},
	setPaymentSources: (state: VuexState, sources: PaymentSources[]): void => {
		state.payment_sources = sources.map((source: PaymentSources) => {
			if (source.nickname === undefined) {
				source.nickname = '';
			}

			return source;
		});
	},
	setRecurringPayment: (state: VuexState, sources: any[]): void => {
		state.recurring_payment = sources.map((source: any) => {
			return source;
		});
	},
	addPaymentSource: (state: VuexState, source: PaymentSources): void => {
		source.nickname = '';
		state.payment_sources.push(source);
	},
	addRecurringPayment: (state: VuexState, source: any): void => {
		if (
			state.recurring_payment.findIndex(
				p =>
					p.policy_num === source.policy_num &&
					p.payment_method === source.payment_method,
			) < 0
		) {
			state.recurring_payment.push(source);
		}
	},
	editRecurringPayment: (state: VuexState, source: any): void => {
		const index = state.recurring_payment.findIndex(p => p.policy_num === source.policy_num);
		state.recurring_payment[index] = source;
	},
	removeRecurringPayment: (state: VuexState, policyNumber: any): void => {
		state.recurring_payment.splice(
			state.recurring_payment.findIndex(p => p.policy_num === policyNumber),
			1,
		);
	},
	setPaymentHistory: (state: VuexState, history: PaymentHistory[]): void => {
		state.payment_history = history;
	},
	addPaymentHistory: (state: VuexState, history: PaymentHistory): void => {
		state.payment_history.unshift(history);
	},
	setBalance: (state: VuexState, balance: PaymentBalance): void => {
		state.balance = balance;
	},
	updatePaymentSource: (
		state: VuexState,
		payload: { id: string; data: PaymentSources },
	): void => {
		console.log(payload);
		state.payment_sources.map(source => {
			if (source.id === payload.id) {
				return payload.data;
			}
		});

		// state.payment_sources.forEach( (source, index) => {
		// 	if( source.id === payload.id ) {
		// 		state.payment_sources[index] = payload.data
		// 	}
		// } );
	},
	setRecurringData: (state: VuexState, sources: any[]): void => {
		state.recurring_data = sources;
	},
	updateRecurringData: (state: VuexState, payload: { status: any; index: any }): void => {
		state.recurring_data[payload.index].status = payload.status;
	},
};

const actions = {
	clearData: ({ commit }: any): void => {
		commit('setPaymentSources', []);
		commit('setPaymentHistory', []);
		commit('setRecurringPayment', []);
		commit('setRecurringData', []);
		commit('setBalance', {
			balance: '',
			due: '',
		});
	},
	async setPaymentSources({ commit }: any): Promise<void> {
		try {
			// Fetch Payment Methods
			const fetchPaymentMethods = await BillPay.getPaymentMethods();

			if (fetchPaymentMethods.data.length > 0) {
				commit('setPaymentSources', fetchPaymentMethods.data);
			}
		} catch (e) {
			throw new Error(e);
		}
	},
	async getRecurringData({ commit }: any): Promise<void> {
		try {
			const getRecurring = await Staff.getRecurring();

			if (getRecurring.data.length > 0) {
				this.recurring = getRecurring.data.map(data => {
					const formater = new Intl.NumberFormat('en-US', {
						style: 'currency',
						currency: 'USD',
					});

					data.amount = formater.format(data.amount);

					if (data.status === undefined) {
						data.status = 'undefined';
					} else {
						data.status = data.status.toString();
					}

					return data;
				});
				commit('setRecurringData', this.recurring);
			}
		} catch (e) {
			throw new Error(e);
		}
	},
	async getRecurringPolicies({ commit, state, rootState }: any): Promise<void> {
		const policies: PolicyUi[] = rootState.Policy.policies; // Get policies out of rootState. We should have them by now
		try {
			await Promise.all(
				policies.map(async policy => {
					if (
						!(
							policy.recurring_enabled &&
							policy.recurring_data &&
							policy.recurring_data.payor_source
						)
					) {
						console.log('not setup: ', policy.policy_number);
						return policy;
					}

					console.log('setup: ', policy.policy_number);

					// Get withdrawlDate
					const { data } = await Recurring.getRecurringPayment(policy.policy_number);

					const recurringSetup = {
						policy_num: policy.policy_number,
						payment_method: policy.recurring_data.payor_source,
						withdrawl_date: data.recurringDate,
						setupBy: policy.recurring_data.name,
						policyName: policy.riskName,
					};

					commit('addRecurringPayment', recurringSetup);
				}),
			);
		} catch (e) {
			console.log(e);
			throw new Error('There was an error fetching the setup recurring policies.');
		}
	},
};

export { state, getters, mutations, actions };
